import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useProducts } from "../../services/productsService";
import TableService from "../../services/tableService";
import { PRODUCTS_TABLE_NAME, useProductsTableFieldsConfig } from "./ProductsTableFieldsConfig";
import { TextFilter } from "../../components/common/TextFilter";

export function ProductsPage(props) {
    const { t } = useTranslation();
    const PRODUCTS_TABLE_FIELDS_CONFIG = useProductsTableFieldsConfig();

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [checkedAll, setCheckedAll] = useState(false);

    const hasEditPermission = true;

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: productRows, isLoading: isProductsLoading, refetch } = useProducts(searchParams);

    useEffect(() => {
        if (productRows?.results) {
            setDataLocal(productRows.results);
            setRecordsCount(productRows.count);
        }
    }, [productRows, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
        // This function can be used for row buttons if needed
    }, []);

    const productsDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            PRODUCTS_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[PRODUCTS_TABLE_NAME], // @ts-ignore
            columnsOrdersForTables?.[PRODUCTS_TABLE_NAME],
        );
    }, [PRODUCTS_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                PRODUCTS_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[PRODUCTS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[PRODUCTS_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[PRODUCTS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[PRODUCTS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            PRODUCTS_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const toolbarComponent = () => (
        <UniversalToolBarWithDialogs
            pageName={props.pageName}
            tableConfig={{
                name: PRODUCTS_TABLE_NAME,
                config: PRODUCTS_TABLE_FIELDS_CONFIG,
            }}
            filteringData={() => {}}
            refreshTable={refetch}
            // @ts-ignore
            resetPageNumber={resetPageNumber}
            hiddenColumnsForTables={hiddenColumnsForTables}
            setHiddenColumnsForTables={setHiddenColumnsForTables}
            columnsOrdersForTables={columnsOrdersForTables}
            setColumnsOrdersForTables={setColumnsOrdersForTables}
            itemType={"product"}
            showMassActionButton={false}
            showOpenCloseFilterButton={false}
            showCleanfiltersButton={true}
            showMyFiltersButton={true}
            showSearchButton={true}
            style={{ marginBlock: "5px" }}
            showCreateButton={false}
            mdGridButton={hasEditPermission ? 2.25 : 3.75}
            showImportList={false}
            showRemoveLeadButton={false}
        />
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            selectedItemId={selectedRowId}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    PRODUCTS_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[PRODUCTS_TABLE_NAME]
                        ? // @ts-ignore
                          hiddenColumnsForTables?.[PRODUCTS_TABLE_NAME]
                        : [],
                ), // @ts-ignore
                columnsOrdersForTables?.[PRODUCTS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[PRODUCTS_TABLE_NAME]
                    : [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"products/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={productsDataExportHeaders}
            exportToFileFileName={`${t("products.exportFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            checkedAll={checkedAll}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            setCheckedAll={setCheckedAll}
            setSelectedRowId={setSelectedRowId}
            isLoading={isProductsLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.products")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={4}>
                        <TextFilter
                            filterKey={"master"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            resetPageNumber={resetPageNumber}
                            withPagination
                            placeholder={t("products.masterFilterPlaceholder") ?? undefined}
                        />
                </Grid>
                <Grid item container direction="row" alignItems="center" spacing={10}>
                    <Grid item xs={2}>
                        <InvestmentFilter
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                        />
                    </Grid>
                    <Grid item xs={10}>
                        {toolbarComponent()}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isProductsLoading && !dataLocal.length}>
                        {tableComponent()}
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
